import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { createCheckIn } from '@/lib/checkin';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Shield, Swords, Calendar as CalendarIcon, Clock, Plus, Trash2, ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import MobileSelectSheet from '@/components/MobileSelectSheet';

const MAX_SLOTS = 3;

function CountdownTimer({ deadline }) {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const update = () => {
      const d = String(deadline);
      const diff = new Date(d.endsWith('Z') || d.includes('+') ? d : d + 'Z') - new Date();
      if (diff <= 0) { setTimeLeft('Expiré'); return; }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  return <span className="font-mono">{timeLeft}</span>;
}

export default function DuelActionButtons({ duel, playground, lastCheckIn, onSuccess, onError }) {
  const queryClient = useQueryClient();
  const { getLocation } = useOptimizedGeolocation();
  const [showCantNow, setShowCantNow] = useState(false);
  const [showProposeDialog, setShowProposeDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState('14');
  const [selectedMinute, setSelectedMinute] = useState('00');
  const [proposedSlots, setProposedSlots] = useState([]);
  const [showHourSelect, setShowHourSelect] = useState(false);
  const [showMinuteSelect, setShowMinuteSelect] = useState(false);

  // Accept duel mutation (check-in + accept en 1 clic)
  const acceptDuelMutation = useMutation({
    mutationFn: async () => {
      const location = await getLocation({ useCache: false, highAccuracy: true, timeout: 5000 });
      const checkinData = await createCheckIn({ playground_id: duel.playground_id || playground?.id, user_lat: location.lat, user_lng: location.lng });
      if (!checkinData.is_valid) throw new Error(`Trop loin ! Distance: ${Math.round(checkinData.distance_m)}m. Rapproche-toi du terrain.`);
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/acceptDuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: duel.id, checkin_id: checkinData.checkin_id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      queryClient.invalidateQueries({ queryKey: ['openDuelsCount'] });
      if (onSuccess) onSuccess({ type: 'success', title: 'Match lancé !', message: 'Le duel est maintenant actif. Bonne chance !' });
    },
    onError: (error) => {
      if (onError) onError({ type: 'error', title: 'Erreur', message: error.message || 'Impossible d\'accepter le duel' });
    }
  });

  // Refuse duel mutation
  const refuseDuelMutation = useMutation({
    mutationFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/refuseDuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: duel.id })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Erreur');
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      queryClient.invalidateQueries({ queryKey: ['openDuelsCount'] });
      if (onSuccess) onSuccess({ type: 'success', title: 'Duel refusé', message: 'Le terrain est ouvert pendant 2h.' });
    },
    onError: (error) => {
      if (onError) onError({ type: 'error', title: 'Erreur', message: error.message || 'Impossible de refuser' });
    }
  });

  // Propose time slots mutation
  const proposeSlotsMutation = useMutation({
    mutationFn: async () => {
      if (proposedSlots.length === 0) throw new Error('Au moins un créneau requis');
      const { error } = await supabase.from('court_duels').update({
        proposed_time_slots: proposedSlots.map(slot => slot.toISOString()),
        slots_proposed_at: new Date().toISOString(),
        slot_selection_deadline_at: new Date(Date.now() + 15 * 60 * 1000).toISOString(),
        status: 'PENDING_SLOT_SELECTION'
      }).eq('id', duel.id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['duel', playground?.id] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      setShowProposeDialog(false);
      setShowCantNow(false);
      setProposedSlots([]);
      setSelectedDate(null);
      if (onSuccess) onSuccess({ type: 'success', title: 'Créneaux proposés', message: "L'attaquant a 15 minutes pour choisir." });
    },
    onError: (error) => {
      if (onError) onError({ type: 'error', title: 'Erreur', message: error.message || 'Échec de la proposition' });
    }
  });

  const addSlot = () => {
    if (!selectedDate || proposedSlots.length >= MAX_SLOTS) return;
    const newSlot = new Date(selectedDate);
    newSlot.setHours(parseInt(selectedHour), parseInt(selectedMinute), 0, 0);
    if (proposedSlots.some(slot => slot.getTime() === newSlot.getTime())) return;
    setProposedSlots([...proposedSlots, newSlot].sort((a, b) => a - b));
    setSelectedDate(null);
  };

  const removeSlot = (index) => {
    setProposedSlots(proposedSlots.filter((_, i) => i !== index));
  };

  const isPending = acceptDuelMutation.isPending || refuseDuelMutation.isPending || proposeSlotsMutation.isPending;

  return (
    <>
      {/* Header: Tu es défié ! */}
      <div className="text-center mb-4">
        <Swords className="w-10 h-10 text-[#6E2BFF] mx-auto mb-2 animate-pulse" />
        <p className="text-white font-black text-lg">
          {duel.attacker_name || 'Un joueur'} te défie !
        </p>
        {duel.accept_deadline_at && (
          <p className="text-[#B3B6C2] text-sm mt-1">
            Expire dans <CountdownTimer deadline={duel.accept_deadline_at} />
          </p>
        )}
      </div>

      <div className="space-y-3">
        {/* Action principale : J'y suis, je défends */}
        <Button
          className="w-full h-14 bg-green-600 hover:bg-green-500 text-white font-bold text-base uppercase transition-all shadow-lg hover:shadow-lg hover:shadow-green-600/40"
          onClick={() => acceptDuelMutation.mutate()}
          disabled={isPending}
        >
          <Shield className="w-5 h-5 mr-2" />
          {acceptDuelMutation.isPending ? 'Localisation...' : "J'y suis, je défends"}
        </Button>

        {/* Action secondaire : Je ne peux pas maintenant */}
        {!showCantNow ? (
          <Button
            variant="outline"
            className="w-full h-12 border-[#2A2D3A] text-[#B3B6C2] hover:text-white hover:bg-[#1B1E28] font-medium"
            onClick={() => setShowCantNow(true)}
            disabled={isPending}
          >
            Je ne peux pas maintenant
            <ChevronDown className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <div className="space-y-2 bg-[#0A0B10] rounded-xl p-3 border border-[#2A2D3A]">
            {/* Proposer des créneaux */}
            <Button
              className="w-full h-11 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm uppercase"
              onClick={() => setShowProposeDialog(true)}
              disabled={isPending}
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              Proposer des créneaux
            </Button>

            {/* Refuser — discret */}
            <button
              className="w-full text-center text-red-400/70 hover:text-red-400 text-xs py-2 transition-colors"
              onClick={() => {
                if (confirm('Refuser rend ton terrain ouvert pendant 2h. N\'importe qui peut te le prendre.\n\nConfirmer ?')) {
                  refuseDuelMutation.mutate();
                }
              }}
              disabled={isPending}
            >
              {refuseDuelMutation.isPending ? 'Refus en cours...' : 'Refuser le défi'}
            </button>
            <p className="text-[10px] text-red-400/40 text-center">
              Ton terrain sera ouvert 2h à tous les joueurs
            </p>
          </div>
        )}
      </div>

      {/* Propose Time Slots Dialog */}
      <Dialog open={showProposeDialog} onOpenChange={setShowProposeDialog}>
        <DialogContent className="bg-[#13151D] border-2 border-amber-600 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-400 font-bold uppercase">
              <CalendarIcon className="inline w-5 h-5 mr-2 mb-1" />
              Proposer des créneaux
            </DialogTitle>
            <DialogDescription className="text-[#B3B6C2]">
              Jusqu'à {MAX_SLOTS} créneaux. L'attaquant aura 15 min pour choisir.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {proposedSlots.length > 0 && (
              <div className="bg-[#0A0B10] rounded-lg p-3 border border-amber-600/30">
                <p className="text-xs font-bold text-amber-400 uppercase mb-2">
                  Créneaux ({proposedSlots.length}/{MAX_SLOTS})
                </p>
                <div className="space-y-2">
                  {proposedSlots.map((slot, index) => (
                    <div key={index} className="flex items-center justify-between bg-[#13151D] rounded p-2">
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white">{format(slot, 'EEEE d MMMM', { locale: fr })}</p>
                        <p className="text-xs text-[#B3B6C2]">{format(slot, 'HH:mm', { locale: fr })}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeSlot(index)} className="text-red-500 hover:text-red-400 p-1">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {proposedSlots.length < MAX_SLOTS && (
              <>
                <div className="bg-[#0A0B10] rounded-lg p-3 border border-amber-600/30">
                  <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} disabled={(date) => date < new Date()} locale={fr} className="rounded-md" />
                </div>

                {selectedDate && (
                  <div className="bg-[#0A0B10] rounded-lg p-4 border border-amber-600/30">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <p className="text-sm font-bold text-white uppercase">Heure</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <MobileSelectSheet open={showHourSelect} onOpenChange={setShowHourSelect} value={selectedHour} onValueChange={setSelectedHour}
                          options={Array.from({ length: 24 }, (_, i) => i).map((h) => ({ value: h.toString().padStart(2, '0'), label: h.toString().padStart(2, '0') }))}
                          label="Heure" placeholder="HH" />
                      </div>
                      <span className="text-white font-bold">:</span>
                      <div className="flex-1">
                        <MobileSelectSheet open={showMinuteSelect} onOpenChange={setShowMinuteSelect} value={selectedMinute} onValueChange={setSelectedMinute}
                          options={['00', '15', '30', '45'].map((m) => ({ value: m, label: m }))}
                          label="Minute" placeholder="MM" />
                      </div>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs text-[#B3B6C2] mb-2">
                        {format(selectedDate, 'EEEE d MMMM yyyy', { locale: fr })} à {selectedHour}:{selectedMinute}
                      </p>
                      <Button className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm" onClick={addSlot}>
                        <Plus className="w-4 h-4 mr-2" /> Ajouter
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1 border-[#B3B6C2] text-[#B3B6C2] hover:bg-[#1B1E28]"
                onClick={() => { setShowProposeDialog(false); setProposedSlots([]); setSelectedDate(null); }}
                disabled={proposeSlotsMutation.isPending}>
                Annuler
              </Button>
              <Button className="flex-1 bg-amber-600 hover:bg-amber-500 text-white font-bold"
                onClick={() => proposeSlotsMutation.mutate()}
                disabled={proposedSlots.length === 0 || proposeSlotsMutation.isPending}>
                {proposeSlotsMutation.isPending ? 'Envoi...' : `Proposer (${proposedSlots.length})`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
